import { mockId, mulberry32, pick, pickWeighted, randInt } from "./random"
import { mockSites } from "./sites"

const MANUFACTURERS = [
  "Huawei",
  "Schneider",
  "SMA",
  "ABB",
  "Siemens",
  "Fronius",
  "ottomotive-loftty",
  "ottomotive-loffty",
] as const
const INSTALLER_MANUFACTURERS = new Set(["ottomotive-loftty", "ottomotive-loffty"])
const INSTALLER_MODELS = ["three-phase-loftty-12kW", "single-phase-loftty-06kW"] as const
// Backend (Hems-Backend) enum değerlerini de mock'a ekledik — 2026-04-21.
// Eski EdgeBox/HEMS/CNI_EMS/Inverter: legacy mock uyumu; Invertor/BMS/Battery/MPPS:
// prod backend tarafından dönen değerler.
const TYPES = [
  "EdgeBox",
  "HEMS",
  "CNI_EMS",
  "Inverter",
  "Invertor",
  "BMS",
  "Battery",
  "MPPS",
] as const

const MODELS_BY_TYPE: Record<(typeof TYPES)[number], string[]> = {
  EdgeBox: ["EB-1000", "EB-2000", "EB-Pro"],
  HEMS: ["HEMS-500", "HEMS-1200", "HEMS-X"],
  CNI_EMS: ["CNI-R1", "CNI-R2", "CNI-Edge"],
  Inverter: ["SUN-8K", "SUN-12K", "SUN-20K", "STP-25K"],
  Invertor: ["INV-5K", "INV-10K", "INV-20K"],
  BMS: ["BMS-48V", "BMS-400V"],
  Battery: ["BAT-5kWh", "BAT-10kWh", "BAT-20kWh"],
  MPPS: ["MPPT-60A", "MPPT-100A"],
}

export type DeviceType = (typeof TYPES)[number]

export interface MockDevice {
  id: string
  thingsBoardDeviceId: string
  manufacturer: string
  modelNumber: string
  serialNumber: string
  typeNumber: string
  deviceType: DeviceType
  qrCode: string
  specifications: { ratedPowerKw: number; firmware: string; phase: "single" | "three" }
  isDeleted: false
  installationDate: string
  calibrationDate: string | null
  nextCheckoutDate: string | null
  warrantyPeriod: number
  warrantyStartDate: string
  siteId: string
  siteName: string
  created: string
  lastModified: string | null
  // B-017 — backend compute. Mock'ta `lastModified`'ı kopyalayarak status
  // dağılımını koruyoruz.
  lastSeenAt: string | null
}

const DEVICES_SEED = 42

function generate(): MockDevice[] {
  const rng = mulberry32(DEVICES_SEED)
  const devices: MockDevice[] = []
  const sitesWithCap = mockSites
    .filter((s) => s.deviceCount > 0)
    .flatMap((s) => Array.from({ length: s.deviceCount }, () => s))

  const now = Date.now()
  const total = 150
  for (let i = 0; i < total; i++) {
    // The first ten devices are reserved for the deterministic scope scenario;
    // installer devices are seeded after that and all go directly to Netzr.
    const manufacturer = pick(
      rng,
      i < 10 ? MANUFACTURERS.slice(0, 6) : MANUFACTURERS,
    )
    const deviceType = pick(rng, TYPES)
    const model = INSTALLER_MANUFACTURERS.has(manufacturer)
      ? pick(rng, INSTALLER_MODELS)
      : pick(rng, MODELS_BY_TYPE[deviceType])
    const site = sitesWithCap[i % sitesWithCap.length] ?? mockSites[0]
    if (!site) {
      continue
    }
    const installDaysAgo = randInt(rng, 30, 900)
    const installDate = new Date(now - installDaysAgo * 24 * 3600_000).toISOString()
    const createdDaysAgo = Math.min(installDaysAgo, randInt(rng, 10, 400))
    const createdAt = new Date(now - createdDaysAgo * 24 * 3600_000).toISOString()

    // lastModified dağılımı: 65% online (<5dk), 15% stale (5-15dk), 15% offline (>15dk), 5% null
    const bucket = pickWeighted(rng, [
      { value: "online", weight: 65 },
      { value: "stale", weight: 15 },
      { value: "offline", weight: 15 },
      { value: "standby", weight: 5 },
    ] as const)
    let lastModified: string | null
    if (bucket === "online") {
      lastModified = new Date(now - randInt(rng, 0, 4 * 60_000)).toISOString()
    } else if (bucket === "stale") {
      lastModified = new Date(now - randInt(rng, 5 * 60_000, 14 * 60_000)).toISOString()
    } else if (bucket === "offline") {
      lastModified = new Date(now - randInt(rng, 20 * 60_000, 48 * 3600_000)).toISOString()
    } else {
      lastModified = null
    }

    const id = mockId(rng, "dev")
    devices.push({
      id,
      // Mock'ta ThingsBoardDeviceId aynı değeri alıyor (gerçek backend'de ayrı
      // bir UUID, DeviceData endpoint filter'ı için). Frontend bu alanı kullansın.
      thingsBoardDeviceId: id,
      manufacturer,
      modelNumber: model,
      serialNumber: `${manufacturer.slice(0, 3).toUpperCase()}-${randInt(rng, 100000, 999999)}`,
      typeNumber: `TYPE-${deviceType}-${randInt(rng, 1, 9)}`,
      deviceType,
      qrCode: `QR-${mockId(rng, "q").slice(-12)}`,
      specifications: {
        ratedPowerKw: pick(rng, [5, 8, 12, 20, 25, 50]),
        firmware: `${randInt(rng, 1, 4)}.${randInt(rng, 0, 9)}.${randInt(rng, 0, 99)}`,
        phase: pick(rng, ["single", "three"] as const),
      },
      isDeleted: false,
      installationDate: installDate,
      calibrationDate: rng() > 0.4 ? installDate : null,
      nextCheckoutDate: null,
      warrantyPeriod: pick(rng, [12, 24, 36, 60]),
      warrantyStartDate: installDate,
      siteId: site.id,
      siteName: site.name,
      created: createdAt,
      lastModified,
      lastSeenAt: lastModified,
    })
  }
  return devices
}

export const mockDevices: MockDevice[] = generate()

export function getMockDevice(id: string): MockDevice | undefined {
  return mockDevices.find((d) => d.id === id)
}

export function getMockDevicesBySite(siteId: string): MockDevice[] {
  return mockDevices.filter((d) => d.siteId === siteId)
}
